import first from "lodash/first";
import { CodeMapSet } from "@app/codemaps";
import InfoIndicator from "@app/shared/InfoIndicator";
import { AccessCriteriaGranularityEnum, TemplateCollectionMetadata, TemplateSpecialtyGroup } from "@app/templates";
import { JSX, RefObject, useEffect, useState } from "react";
import { ResourceType } from "@app/resources";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";
import { UserInfo } from "@app/user/UserContext";
import TemplateService from "@app/templates/templateService";
import SpecialtyGroupsTable from "@app/projects/create/SpecialtyGroupsTable";
import { TimeDistanceCalculatorType, EditPerspective } from "@app/projects/enumerators";
import { EditTemplateOptionsHelper } from "@app/projects/network-assessment/EditTemplateOptionsHelper";
import Project from "@app/projects/project";
import { timeDistanceCalculatorTypeDisplayMap } from "@app/projects/timeDistanceCalculatorTypeDisplayMap";

interface EditAdequacyTemplateOptionProps {
  shared: boolean;
  disabled: boolean;
  useAdequacy: boolean;
  project: Project;
  previousTimeDistanceCalculatorType: TimeDistanceCalculatorType;
  templateAccessGranularity: AccessCriteriaGranularityEnum;
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  projectAccessCodeMapSets: CodeMapSet[];
  expandSpecialtyMileageCodeMapSets: CodeMapSet[];
  specialtyCodeMapSets: CodeMapSet[];
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
  disagreedExcludedSpecialtyGroupCodes: string[];
  disagreedTelehealthSpecialtyGroupCodes?: string[];
  templateCollections: TemplateCollectionMetadata[];
  onTemplateCollectionChange: (templateCollectionId: number) => void;
  onTemplateCorrelationIdChange: (templateCorrelationId: string, userRoles: string[]) => void;
  onTimeDistanceCalculatorTypeChange: (timeDistanceCalculatorType: TimeDistanceCalculatorType) => void;
  onAccessCodeMapSetChange: (accessCodeMapSetId: number) => void;
  onExpandSpecialtyMileageChange: (value: number) => void;
  onMarketSpecialtyCodeMapChange: (value: number) => void;
  onNetworkSpecialtyCodeMapChange: (value: number) => void;
  onSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  onTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCodes: string[]) => void;
  user: UserInfo;
  isBulkEdit?: boolean;
  adequacyTemplateCollectionIdRef: RefObject<HTMLSelectElement>;
  isZipProject: boolean;
  perspective: EditPerspective;
  showNetworkSpecialtyCodeMap: boolean;
}

const EditAdequacyTemplateOptions = (props: EditAdequacyTemplateOptionProps): JSX.Element => {
  const [priorityTemplates, setPriorityTemplates] = useState([]);

  const templateHelper = new EditTemplateOptionsHelper();
  const templateService = new TemplateService();
  const templateGranularityPrefix = templateHelper.getTemplateGranularityPrefix(props.templateAccessGranularity);

  useEffect(() => {
    const getPriorityTemplates = async () => {
      const templates = await templateService.getPriorityCmsTemplates();
      setPriorityTemplates(templates);
    };
    getPriorityTemplates();
  }, []);

  const renderAdequacyTemplateCollections = () => {
    if (props.shared) {
      return (
        <QESTextInput
          label="Adequacy Template Collection"
          className="form-control"
          disabled={true}
          value={`${props.project.templateName} - ${props.project.templateDescription}`}
          testId="edit-template-options__adequacy-template-collection-text-box"
        />
      );
    } else {
      return (
        <QESSelect
          label="Adequacy Template Collection"
          className="form-control"
          disabled={props.disabled}
          value={props.project.adequacyTemplateCollectionId || ""}
          onChange={(e) => props.onTemplateCollectionChange(+(e.target.value))}
          testId="edit-template-options__adequacy-template-collection-select"
          qesRef={props.adequacyTemplateCollectionIdRef}
        >
          {props.project.adequacyTemplateCollectionId == -1 && <option value="-1" key="-1">Different Adequacy Template Collection Being Used</option>}
          <option value="0" key="0">Select an Adequacy Template Collection</option>
          {props.templateCollections.filter((collection) => {
            if (props.perspective === EditPerspective.Edit) {
              if (props.isZipProject && collection.resource.resourceType !== ResourceType.AdequacyZipTemplateCollection) {
                return false;
              } else if (!props.isZipProject && collection.resource.resourceType === ResourceType.AdequacyZipTemplateCollection) {
                return false;
              }
            }
            return true;
          }).map((collection) => (
            <option key={collection.resource.resourceId} value={collection.resource.resourceId}>
              {`${collection.resource.name} - ${collection.resource.description}`}
            </option>
          ))}
        </QESSelect>
      );
    }
  }

  const renderProjectAdequacyTemplateCorrelationIdsList = () => {
    if (props.shared) {
      return (
        <QESTextInput
          label="Adequacy Template"
          className="form-control"
          disabled={true}
          value={props.project.adequacyTemplateCorrelationId}
          testId="edit-template-options__adequacy-template-correlation-list-text-box"
        />
      );
    } else {
      const selectedProjectCollection = first(props.templateCollections.filter((item) => item.templateCollectionId === props.project.adequacyTemplateCollectionId));
      const priorityTemplatesToInclude = priorityTemplates.filter((pt) => selectedProjectCollection?.correlationIds.includes(pt)) || [];
      const sortedNonPriorityTemplates = selectedProjectCollection?.correlationIds.sort().filter((t) => !priorityTemplates.includes(t)) || [];

      return (
        <QESSelect
          label="Adequacy Template"
          className="form-control"
          value={props.project.adequacyTemplateCorrelationId || ""}
          onChange={(e) => props.onTemplateCorrelationIdChange(e.target.value, props.user.roles)}
          disabled={props.disabled}
          testId="edit-template-options__adequacy-template-correlation-list-select"
        >
          {props.project.adequacyTemplateCorrelationId == "-1" && <option value="-1" key="-1">Different Adequacy Template Being Used</option>}
          <option value="0">Select an Adequacy Template</option>
          {priorityTemplatesToInclude.length > 0 && (
            <optgroup label="New Templates">
              {
                priorityTemplatesToInclude.map((t) => (
                  <option key={t} value={t}>{`${t}`}</option>
                ))
              }
            </optgroup>
          )}
          {sortedNonPriorityTemplates.map((option) => (
            <option key={option} value={option}>{`${option}`}</option>
          ))}
        </QESSelect>
      );
    }
  }

  const renderTimeDistanceCalculationType = () => {
    if (props.shared) {
      return (
        <QESTextInput
          label="Time/Distance Calculation Type"
          className="form-control"
          disabled={true}
          value={`${props.project.timeDistanceCalculatorType ? timeDistanceCalculatorTypeDisplayMap[props.project.timeDistanceCalculatorType] : ""}`}
          testId="edit-template-options__time-distance-calculation-type-text-box"
        />
      );
    } else {
      return (
        props.previousTimeDistanceCalculatorType &&
        (
          <QESSelect
            label="Time/Distance Calculation Type"
            className="form-control"
            disabled={props.disabled}
            value={props.project.timeDistanceCalculatorType || ""}
            onChange={(e) => props.onTimeDistanceCalculatorTypeChange(e.target.value as TimeDistanceCalculatorType)}
            testId="edit-template-options__time-distance-calculation-type-select"
          >
            {getTimeDistanceOptions(props.user.roles)}
          </QESSelect>
        )
      );
    }
  }

  const renderOption = (t: TimeDistanceCalculatorType) => {
    return (
      <option key={t} value={t}>{timeDistanceCalculatorTypeDisplayMap[t]}</option>
    );
  };

  const getTimeDistanceOptions = (roles: string[]) => {

    const roleCalculatorTypeMap: { [key: string]: TimeDistanceCalculatorType } = {
      [Roles.ROUTING_DISTANCE_USER]: TimeDistanceCalculatorType.GraphhopperRouting,
      [Roles.STRAIGHT_LINE_DISTANCE_USER]: TimeDistanceCalculatorType.StraightLine,
    };

    const rolesSet = new Set(roles);
    switch (props.previousTimeDistanceCalculatorType) {
      case TimeDistanceCalculatorType.GraphhopperRouting:
        rolesSet.add(Roles.ROUTING_DISTANCE_USER);
        break;
      case TimeDistanceCalculatorType.StraightLine:
        rolesSet.add(Roles.STRAIGHT_LINE_DISTANCE_USER);
        break;
    }

    const roleOptionMap: { [key: string]: JSX.Element } = {
      [Roles.ROUTING_DISTANCE_USER]: renderOption(TimeDistanceCalculatorType.GraphhopperRouting),
      [Roles.STRAIGHT_LINE_DISTANCE_USER]: renderOption(TimeDistanceCalculatorType.StraightLine)
    }
    const options: JSX.Element[] = [];
    if (props.timeDistanceCalculatorTypes.find(t => t === TimeDistanceCalculatorType.Quest)) {
      options.push(renderOption(TimeDistanceCalculatorType.Quest));
    }
    if (props.timeDistanceCalculatorTypes.find(t => t === TimeDistanceCalculatorType.GeographyAware)) {
      options.push(renderOption(TimeDistanceCalculatorType.GeographyAware));
    }
    rolesSet.forEach((role) => {
      if (props.timeDistanceCalculatorTypes && props.timeDistanceCalculatorTypes.find(t => t === roleCalculatorTypeMap[role])) {
        options.push(roleOptionMap[role]);
      }
    });
    return options;
  }

  const renderAccessCodeMapsList = (templateGranularityPrefix: string) => {
    if (props.shared) {
      return (
        <QESTextInput
          label={`${templateGranularityPrefix} to Classification Code Map`}
          className="form-control"
          disabled={true}
          value={props.project.accessCodeMapSetName}
          testId="edit-template-options__access-code-map-set-name-list-text-box"
        />
      );
    } else {

      return (
        <QESSelect
          label={`${templateGranularityPrefix} to Classification Code Map`}
          className="form-control"
          onChange={(e) => props.onAccessCodeMapSetChange(+(e.target.value))}
          value={props.project.accessCodeMapSetId
            ? props.project.accessCodeMapSetId
            : "0"}
          disabled={props.disabled}
          testId="edit-template-options__access-code-map-set-name-list-select"
        >
          {props.project.accessCodeMapSetId == -1 && <option value="-1" key="-1">Different {templateGranularityPrefix} to Classification Code Map Being Used</option>}
          <option value="0">Select a {templateGranularityPrefix} to Classification Code Map</option>
          {props.projectAccessCodeMapSets.map((set) => (
            set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }
  }

  const renderNetworkSpecialtyCodeMapsList = () => {
    return (
      <QESSelect
        label="Network Specialty Code Map"
        className="form-control"
        onChange={(e) => props.onNetworkSpecialtyCodeMapChange(+(e.target.value))}
        value={props.project.networkSpecialtyCodeMapSetId
          ? props.project.networkSpecialtyCodeMapSetId
          : "0"}
        disabled={props.disabled}
        testId="edit-template-options__network-specialty-code-map-select"
      >
        {props.project.networkSpecialtyCodeMapSetId == -1 && <option value="-1" key="-1">Different Network Specialty Code Map Being Used</option>}
        <option value="0" key="0">Select a Network Specialty Code Map</option>
        {props.specialtyCodeMapSets.map((set) => (
          set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );
  }

  const renderExpandSpecialtyMileage = () => {
    if (props.shared) {
      return (
        <QESTextInput
          label={(
            <span>
              Expand Specialty Mileage {" "}
              {
                (!props.disabled && props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0) &&
                <InfoIndicator tooltipTitle="Performance Impact - Intended for Nebraska Medicaid: Apply Codemap to expand provider search to 500 county miles." />
              }
            </span>
          )}
          className="form-control"
          disabled={true}
          value={props.project.expandSpecialtyMileageCodeMapSetId}
          testId="edit-template-options__expand-specialty-mileage-text-box"
        />
      );
    } else {

      return (
        <QESSelect
          label={(
            <span>
              Expand Specialty Mileage {" "}
              {
                (!props.disabled && props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0) &&
                <InfoIndicator tooltipTitle="Performance Impact - Intended for Nebraska Medicaid: Apply Codemap to expand provider search to 500 county miles." />
              }
            </span>
          )}
          className="form-control"
          onChange={(e) => props.onExpandSpecialtyMileageChange(+(e.target.value))}
          value={props.project.expandSpecialtyMileageCodeMapSetId
            ? props.project.expandSpecialtyMileageCodeMapSetId
            : "0"}
          disabled={props.disabled || !(props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0)}
          testId="edit-template-options__expand-specialty-mileage-select"
        >
          {props.project.expandSpecialtyMileageCodeMapSetId == -1 && <option value="-1" key="-1">Different Expand Specialty Mileage Being Used</option>}
          {
            props.expandSpecialtyMileageCodeMapSets && props.expandSpecialtyMileageCodeMapSets.length > 0 && (
            <option value="0" key="0">Select an Expand Specialty Mileage</option>)
          }          
          {props.expandSpecialtyMileageCodeMapSets.map((set) => (
            set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }
  }

  const renderMarketSpecialtyCodeMapsList = () => {
    return (
      <QESSelect
        label="Market Specialty Code Map"
        className="form-control"
        onChange={(e) => props.onMarketSpecialtyCodeMapChange(+(e.target.value))}
        value={props.project.marketSpecialtyCodeMapSetId
          ? props.project.marketSpecialtyCodeMapSetId
          : "0"}
        disabled={props.disabled}
        testId="edit-template-options__market-specialty-code-map-select"
      >
        {props.project.marketSpecialtyCodeMapSetId == -1 && <option value="-1" key="-1">Different Market Specialty Code Map Being Used</option>}
        <option value="0" key="0">Select a Market Specialty Code Map</option>
        {props.specialtyCodeMapSets.map((set) => (
          set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );
  }

  return (
    <>
      {props.useAdequacy && (
        <>
          <div className="form-group row">
            <div className="col-12 col-sm-9 col-lg-8 offset-2">
              {renderAdequacyTemplateCollections()}
            </div>
          </div>

          <div className="form-group row">
            <div className="col-12 col-sm-9 col-lg-8 offset-2">
              {renderProjectAdequacyTemplateCorrelationIdsList()}
            </div>
          </div>

          {((props.shared && props.useAdequacy) || !props.isZipProject) &&
            <div className="form-group row">
              <div className="col-12 col-sm-9 col-lg-8 offset-2">
                {renderExpandSpecialtyMileage()}
              </div>
            </div>
          }

          <div className="form-group row">
            <div className="col-12 col-sm-9 col-lg-8 offset-2">
              {renderTimeDistanceCalculationType()}
            </div>
          </div>
        </>
      )}

      {((props.shared && props.useAdequacy && props.project.accessCodeMapSetName) || (props.projectAccessCodeMapSets && props.projectAccessCodeMapSets.length > 0)) &&
        <div className="form-group row">
          <div className="col-12 col-sm-9 col-lg-8 offset-2">
            {renderAccessCodeMapsList(templateGranularityPrefix)}
          </div>
        </div>
      }

      {(!props.shared && (props.specialtyCodeMapSets && props.specialtyCodeMapSets.length > 0) && !!props.project.adequacyTemplateCorrelationId
        && props.project.adequacyTemplateCorrelationId !== "0" && props.showNetworkSpecialtyCodeMap && !props.isZipProject) &&
        <div className="form-group row">
          <div className="col-12 col-sm-9 col-lg-8 offset-2">
            {renderNetworkSpecialtyCodeMapsList()}
          </div>
        </div>
        }


      {(!props.shared && (props.specialtyCodeMapSets && props.specialtyCodeMapSets.length > 0) && !!props.project.adequacyTemplateCorrelationId && props.project.adequacyTemplateCorrelationId !== "0") &&
        <div className="form-group row">
          <div className="col-12 col-sm-9 col-lg-8 offset-2">
            {renderMarketSpecialtyCodeMapsList()}
          </div>
        </div>
      }

      {!props.shared && (
        <>
          {props.sortedSpecialtyGroups && props.sortedSpecialtyGroups.length > 0
            && (props.project.adequacyTemplateCollectionId || props.project.accuracySpecialtyDefinitionId)
            && (
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <SpecialtyGroupsTable
                    canEdit={!props.disabled}
                    excludedSpecialtyGroupCodes={props.project.excludedSpecialtyGroupCodes}
                    handleSpecialtyGroupsChange={props.onSpecialtyGroupsChange}
                    handleTelehealthSpecialtyGroupsChange={props.onTelehealthSpecialtyGroupsChange}
                    specialtyGroups={props.sortedSpecialtyGroups}
                    telehealthSpecialtyGroupCodes={props.project.telehealthSpecialtyGroupCodes}
                    disagreedExcludedSpecialtyGroupCodes={props.disagreedExcludedSpecialtyGroupCodes}
                    disagreedTelehealthSpecialtyGroupCodes={props.disagreedTelehealthSpecialtyGroupCodes}
                  />
                </div>
              </div>
            )}
        </>
      )}
    </>
  );
};

export default EditAdequacyTemplateOptions;